/**
 * Centralized HTTP client for API requests
 *
 * Provides consistent API calling patterns with CSRF token handling,
 * error handling, and credential management.
 */

import { getAccessPausedPath } from '@/lib/accessPaused';
import {
  getBillingRequiredPath,
  isBillingExemptPath,
  isBillingRequiredPath,
} from '@/lib/billingRequired';
import { getApiBaseUrl } from '@/lib/config';
import { recordRequest } from '@/lib/diagnostics/requestLog';
import { getCSRFToken } from '@/lib/django';
import { ApiError, NetworkError, TimeoutError } from '@/lib/errors';

// Re-export for convenience
export { getApiBaseUrl };

/**
 * Delay helper for simulating network latency in development
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Network delay in milliseconds for development mode.
 * Configurable via VITE_DEV_NETWORK_DELAY_MS env var.
 * Simulates realistic latency for international/mobile users.
 */
const DEV_NETWORK_DELAY_MS = import.meta.env.VITE_DEV_NETWORK_DELAY_MS
  ? parseInt(import.meta.env.VITE_DEV_NETWORK_DELAY_MS, 10)
  : 250; // Simulate realistic network latency in dev

/**
 * Low-level fetch wrapper with standard defaults.
 *
 * Automatically handles:
 * - Base URL prefixing (unless URL is already absolute)
 * - credentials: 'include'
 * - CSRF token for non-GET requests
 * - Dev mode network delay simulation
 * - Automatic request timeout via AbortController
 *
 * Returns raw Response - callers handle parsing and error handling.
 *
 * @param url - API endpoint path (e.g., '/api/orgs/1/contacts/') or absolute URL
 * @param options - Standard RequestInit options
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @returns Promise with raw Response
 * @throws {TimeoutError} when the request exceeds the timeout
 * @throws {NetworkError} when the underlying fetch rejects (offline/DNS)
 */
export async function fetchWithDefaults(
  url: string,
  options: RequestInit = {},
  timeout: number = 30000
): Promise<Response> {
  const API_BASE_URL = getApiBaseUrl();

  // Determine if URL needs base URL prefix
  const fullUrl =
    url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `${API_BASE_URL}${url}`;

  // Merge options with defaults
  const mergedOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
    },
  };

  // Add CSRF token for non-GET requests
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET') {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      (mergedOptions.headers as Record<string, string>)['X-CSRFToken'] =
        csrfToken;
    }
  }

  // Simulate network latency in development mode (skip in test environment)
  if (
    import.meta.env.DEV &&
    !import.meta.env.VITEST &&
    DEV_NETWORK_DELAY_MS > 0
  ) {
    await delay(DEV_NETWORK_DELAY_MS);
  }

  // Set up AbortController for timeout
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

  // Track listener reference for cleanup
  let callerSignalListener: (() => void) | null = null;

  // Merge timeout signal with any existing signal from options
  // If caller provided a signal, we need to abort on EITHER timeout OR caller abort
  if (options.signal) {
    // Create a new controller that aborts when either signal fires
    const combinedController = new AbortController();

    // Listen for timeout
    timeoutController.signal.addEventListener(
      'abort',
      () => combinedController.abort(),
      { once: true }
    );

    // Listen for caller's signal - check if already aborted first
    if (options.signal.aborted) {
      // Fail immediately with caller's abort reason
      combinedController.abort(options.signal.reason);
    } else {
      // Create listener and track it for cleanup
      callerSignalListener = () =>
        combinedController.abort(options.signal?.reason);
      options.signal.addEventListener('abort', callerSignalListener, {
        once: true,
      });
    }

    mergedOptions.signal = combinedController.signal;
  } else {
    mergedOptions.signal = timeoutController.signal;
  }

  const recordStart =
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  try {
    // Use globalThis.fetch to ensure we pick up any test mocks
    const response = await globalThis.fetch(fullUrl, mergedOptions);
    clearTimeout(timeoutId);
    // Clean up listener to prevent memory leak on reused signals
    if (callerSignalListener && options.signal && !options.signal.aborted) {
      options.signal.removeEventListener('abort', callerSignalListener);
    }
    recordRequest({
      method,
      url: fullUrl,
      status: response.status,
      durationMs: Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
          recordStart
      ),
    });
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    // Clean up listener even on error
    if (callerSignalListener && options.signal && !options.signal.aborted) {
      options.signal.removeEventListener('abort', callerSignalListener);
    }
    recordRequest({
      method,
      url: fullUrl,
      status: null,
      durationMs: Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
          recordStart
      ),
    });

    // Handle abort errors - distinguish between timeout and caller cancellation
    if (error instanceof Error && error.name === 'AbortError') {
      // Check if this was a caller cancellation (not a timeout)
      if (options.signal?.aborted) {
        // Re-throw the caller's abort error directly
        throw error;
      }

      // Otherwise, it's a timeout. fullUrl is relative whenever the API base
      // is same-origin (''), so the parse needs a base — a bare
      // new URL('/api/…') throws, and a throw here would replace the abort
      // with a bogus TypeError. The base is only consulted for relative
      // URLs, and only the pathname is logged, so a constant placeholder is
      // correct for both forms.
      const { pathname } = new URL(fullUrl, 'http://relative.invalid');
      console.error('Request timeout:', {
        pathname,
        method: method,
        timeoutMs: timeout,
      });
      throw new TimeoutError(`Request timeout after ${timeout}ms`);
    }

    // Caller cancellation with a custom abort reason: the rejection is the
    // caller's reason (not a DOMException named 'AbortError'), so it fell
    // through the branch above. Preserve cancellation semantics — a
    // deliberate cancel must not be misreported as a transient network
    // failure (which would trigger retry/offline UX).
    if (options.signal?.aborted) {
      throw error;
    }

    throw new NetworkError(
      error instanceof Error ? error.message : String(error)
    );
  }
}

/**
 * In-flight request deduplication.
 *
 * Identical read requests issued while an equivalent request is still in
 * flight share the same promise (and therefore the same network call).
 * This is a safety net below the React Query layer: query-cache dedup only
 * collapses fetches that share a queryKey, but several features issue the
 * same READ request under different keys (or refetch in rapid succession
 * when WebSocket invalidation storms hit), producing byte-identical
 * concurrent POSTs to read-only endpoints like crm-objects/query/.
 *
 * Strictly opt-in (`dedupeInFlight`) and only for read semantics — never
 * enable it for a mutating request, where collapsing two calls into one
 * would silently drop a write. Requests carrying an AbortSignal are never
 * deduped: sharing a promise across callers would couple their
 * cancellation (one caller's abort would reject everyone).
 *
 * Entries are removed as soon as the request settles, so the map only ever
 * holds genuinely in-flight requests — this is NOT a response cache.
 */
const inFlightReadRequests = new Map<string, Promise<unknown>>();

/**
 * Hard upper bound on how long a dedup key may live in the map. Under normal
 * settlement the key is removed the instant the request resolves or rejects
 * (well before this fires). It exists solely to guarantee eviction for the
 * intentional "stall" branches in executeApiCall (403 access-paused /
 * billing-required), which return a never-settling promise to suppress a 403
 * flash during a full-page redirect. Without this, such a key would pin a
 * dead promise forever and a later identical call would attach to a promise
 * that never resolves. Set comfortably above the default request timeout so
 * it never evicts a genuinely in-flight request early.
 */
const DEDUP_KEY_MAX_LIFETIME_MS = 60_000;

/**
 * Clear the in-flight dedup map (test helper — called by the global test
 * setup's afterEach). Tests routinely leave requests permanently pending
 * (MSW `delay('infinite')` for loading-state assertions); without this
 * reset, an identical request in a LATER test would be deduped onto the
 * previous test's never-settling promise.
 */
export function __resetInFlightReadRequestsForTesting(): void {
  inFlightReadRequests.clear();
}

/**
 * Generic API call helper with automatic CSRF token handling
 *
 * @param url - API endpoint path (e.g., '/api/orgs/1/contacts/')
 * @param method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param body - Request body (will be JSON stringified)
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @param signal - Optional AbortSignal to cancel the request
 * @param dedupeInFlight - Share the network call with any identical request
 *   (same method + url + body) that is still in flight. Only for READ
 *   requests (GET, or read-only POST query endpoints). Ignored when a
 *   signal is provided.
 * @returns Promise with parsed JSON response
 * @throws {ApiError} on non-2xx responses (carries statusCode and parsed response body)
 */
export async function apiCall<T>(
  url: string,
  method: string = 'GET',
  body?: unknown,
  timeout?: number,
  signal?: AbortSignal,
  dedupeInFlight?: boolean
): Promise<T> {
  if (dedupeInFlight && !signal) {
    const key = `${method} ${url} ${body === undefined ? '' : JSON.stringify(body)}`;
    const existing = inFlightReadRequests.get(key);
    if (existing) {
      return existing as Promise<T>;
    }
    const promise = executeApiCall<T>(url, method, body, timeout, undefined);
    inFlightReadRequests.set(key, promise);
    const cleanup = () => {
      clearTimeout(evictionTimer);
      // Only evict if the entry still points at THIS promise — a later call
      // that started a fresh request under the same key must not be clobbered.
      if (inFlightReadRequests.get(key) === promise) {
        inFlightReadRequests.delete(key);
      }
    };
    // Guaranteed eviction: the stall branches in executeApiCall (403
    // access-paused / billing-required) return a never-settling promise, so
    // then(cleanup) would never fire and the key would leak forever. A bounded
    // timer removes it regardless. cleanup reads evictionTimer lazily (only
    // when invoked), so referencing it before this line is safe.
    const evictionTimer = setTimeout(cleanup, DEDUP_KEY_MAX_LIFETIME_MS);
    // then(cleanup, cleanup) instead of finally() — finally() would create a
    // second promise whose rejection is unhandled when the request fails.
    promise.then(cleanup, cleanup);
    return promise;
  }
  return executeApiCall<T>(url, method, body, timeout, signal);
}

async function executeApiCall<T>(
  url: string,
  method: string,
  body?: unknown,
  timeout?: number,
  signal?: AbortSignal
): Promise<T> {
  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  if (signal) {
    options.signal = signal;
  }

  const response = await fetchWithDefaults(url, options, timeout);

  if (!response.ok) {
    let errorData: Record<string, unknown> = {};
    try {
      errorData = await response.json();
    } catch {
      // Failed to parse error response as JSON - continue with empty errorData
    }
    // Global handler: redirect to the friendly access-paused page for any 403
    // emitted by apps/orgs/middleware.py. Handles both member-level deactivation
    // (error_code=org_access_paused) and org-level deactivation
    // (error_code=org_deactivated).
    const isAccessPaused =
      response.status === 403 &&
      typeof errorData.error_code === 'string' &&
      (errorData.error_code === 'org_access_paused' ||
        errorData.error_code === 'org_deactivated') &&
      typeof errorData.org_id === 'number' &&
      Number.isInteger(errorData.org_id) &&
      errorData.org_id > 0;
    if (isAccessPaused && typeof window !== 'undefined' && window.location) {
      // org_id is narrowed to a positive integer by isAccessPaused above.
      const orgId = errorData.org_id as number;
      const targetPath = getAccessPausedPath(orgId);
      // Tolerate a trailing slash and a missing pathname (test doubles).
      const currentPath = (window.location.pathname ?? '').replace(/\/+$/, '');
      if (currentPath === targetPath) {
        // Already on the access-paused page for this org — a full-page
        // assign to the same URL would reload and re-trigger the 403,
        // looping forever. Skip the navigation but still stall so no
        // 403 toast / error boundary flashes.
        return new Promise<T>(() => {});
      }
      // Use window.location.assign so the navigation is full-page (clears any
      // stale React state from the deactivated session) and bookmarkable.
      window.location.assign(targetPath);
      // Stall the in-flight promise so callers never observe a synchronous 403
      // rejection between assign() and the browser tearing down the page.
      // Without this, generic error handlers can flash a 403 toast or surface
      // an error boundary before navigation completes.
      return new Promise<T>(() => {});
    }

    // Global handler: redirect (or stall, or reject) for 403s emitted by
    // apps/orgs/middleware.py's billing gate. The billing-required page
    // (/{orgId}/billing-required) and the billing settings page
    // (/{orgId}/settings/billing) are both "exempt" — they must remain
    // navigable so an admin can subscribe. On the gate page itself we stall
    // (prevents 403 toast/error-boundary flash from sidebar API calls).
    // On settings/billing we REJECT normally so React Query can transition
    // to error state — stalling there causes infinite loading spinners.
    const isBillingRequired =
      response.status === 403 &&
      typeof errorData.error_code === 'string' &&
      errorData.error_code === 'billing_required' &&
      typeof errorData.org_id === 'number' &&
      Number.isInteger(errorData.org_id) &&
      errorData.org_id > 0;
    if (isBillingRequired && typeof window !== 'undefined' && window.location) {
      const orgId = errorData.org_id as number;
      const targetPath = getBillingRequiredPath(orgId);
      // Tolerate trailing slash and missing pathname (test doubles).
      const currentPath = (window.location.pathname ?? '').replace(/\/+$/, '');
      if (
        isBillingRequiredPath(currentPath) ||
        isBillingRequiredPath(currentPath + '/')
      ) {
        // Already on the billing-required gate page — stall to prevent
        // toast/error-boundary flash from sidebar 403s while the page renders.
        return new Promise<T>(() => {});
      }
      if (
        isBillingExemptPath(currentPath) ||
        isBillingExemptPath(currentPath + '/')
      ) {
        // On /{orgId}/settings/billing — REJECT (do NOT stall, do NOT redirect).
        // React Query needs the error signal to transition loading → error.
        // Fall through to the generic error throw below.
      } else {
        // Any non-exempt page — redirect to the gate page, then stall.
        window.location.assign(targetPath);
        return new Promise<T>(() => {});
      }
    }

    throw new ApiError(
      (typeof errorData.detail === 'string' ? errorData.detail : undefined) ||
        (typeof errorData.error === 'string' ? errorData.error : undefined) ||
        `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorData
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  try {
    return await response.json();
  } catch (parseError) {
    const contentType = response.headers.get('content-type') || 'none';
    const error = new Error(
      `Failed to parse JSON response from ${url} (status: ${response.status}, content-type: ${contentType}): ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
      { cause: parseError }
    );
    throw error;
  }
}

/**
 * Build query string from filters object
 * Omits undefined, null, and empty string values
 *
 * @param filters - Object with filter key-value pairs
 * @returns URL-encoded query string
 */
export function buildQueryString(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  return params.toString();
}

/**
 * Make a GET request to the API
 *
 * @param url - API endpoint path
 * @param filters - Optional query parameters
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @param signal - Optional AbortSignal to cancel the request
 * @returns Promise with parsed JSON response
 */
export async function apiGet<T>(
  url: string,
  filters?: Record<string, unknown>,
  timeout?: number,
  signal?: AbortSignal
): Promise<T> {
  const queryString = filters ? buildQueryString(filters) : '';
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  return apiCall<T>(fullUrl, 'GET', undefined, timeout, signal);
}

/**
 * Make a POST request to the API
 *
 * @param url - API endpoint path
 * @param body - Request body
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @param options.dedupeInFlight - Share the network call with an identical
 *   in-flight request. ONLY for read-only POST endpoints (query/aggregate) —
 *   never for mutations, where collapsing two calls would drop a write.
 * @param options.signal - Optional AbortSignal to cancel the request.
 * @returns Promise with parsed JSON response
 */
export async function apiPost<T>(
  url: string,
  body: unknown,
  timeout?: number,
  options?: { dedupeInFlight?: boolean; signal?: AbortSignal }
): Promise<T> {
  return apiCall<T>(
    url,
    'POST',
    body,
    timeout,
    options?.signal,
    options?.dedupeInFlight
  );
}

/**
 * Make a PUT request to the API
 *
 * @param url - API endpoint path
 * @param body - Request body
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @returns Promise with parsed JSON response
 */
export async function apiPut<T>(
  url: string,
  body: unknown,
  timeout?: number
): Promise<T> {
  return apiCall<T>(url, 'PUT', body, timeout);
}

/**
 * Make a PATCH request to the API
 *
 * @param url - API endpoint path
 * @param body - Request body
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @returns Promise with parsed JSON response
 */
export async function apiPatch<T>(
  url: string,
  body: unknown,
  timeout?: number
): Promise<T> {
  return apiCall<T>(url, 'PATCH', body, timeout);
}

/**
 * Make a DELETE request to the API
 *
 * @param url - API endpoint path
 * @param timeout - Request timeout in milliseconds (default: 30000ms). Use 5s-10s for interactive features, 30s for batch operations
 * @returns Promise with parsed JSON response
 */
export async function apiDelete<T>(url: string, timeout?: number): Promise<T> {
  return apiCall<T>(url, 'DELETE', undefined, timeout);
}
